/*
 * PDP add-ons.
 *
 * Ticking an add-on writes hidden items[N][id] / items[N][quantity] inputs into
 * the product form. The theme builds its add-to-cart payload with
 * new FormData(form), so those inputs ride along with the main product and
 * /cart/add.js adds everything in a single request - each add-on becoming its
 * own cart line item.
 *
 * Verified against the live storefront: a FormData carrying both the single-item
 * fields (id, quantity) and items[] entries adds all of them, and line item
 * properties on the items[] entries are preserved.
 */
(function () {
  'use strict';

  var SELECTORS = {
    container: '[data-product-addons]',
    checkbox: '[data-addon-checkbox]',
    productForm: 'form[data-product-form]',
    injected: 'input[data-addon-input]',
    imageTrigger: '[data-addon-image-trigger]',
    tcLink: '[data-addon-tc]',
    modal: '[data-addons-image-modal]',
    modalImg: '[data-addons-image-modal-img]',
    modalClose: '[data-addons-image-close]'
  };

  function getForm(container) {
    // The add-ons block and the buy buttons live in the same product section.
    var scope = container.closest('product-info, [data-section-id], .product__page') || document;
    return scope.querySelector(SELECTORS.productForm) || document.querySelector(SELECTORS.productForm);
  }

  /*
   * Rewrites the hidden inputs to match the currently ticked boxes.
   * Rebuilt from scratch each time so indexes stay contiguous - Shopify expects
   * items[0], items[1], ... without gaps.
   */
  function syncForm(container) {
    var form = getForm(container);
    if (!form) return;

    form.querySelectorAll(SELECTORS.injected).forEach(function (input) {
      input.remove();
    });

    var checked = container.querySelectorAll(SELECTORS.checkbox + ':checked');
    var index = 0;

    checked.forEach(function (checkbox) {
      var variantId = checkbox.getAttribute('data-addon-variant-id');
      if (!variantId) return;

      var quantity = parseInt(checkbox.getAttribute('data-addon-quantity'), 10);
      if (!quantity || quantity < 1) quantity = 1;

      form.appendChild(makeInput('items[' + index + '][id]', variantId));
      form.appendChild(makeInput('items[' + index + '][quantity]', quantity));
      index++;
    });

    container.dispatchEvent(
      new CustomEvent('product-addons:change', {
        bubbles: true,
        detail: {selectedCount: index}
      })
    );
  }

  function makeInput(name, value) {
    var input = document.createElement('input');
    input.type = 'hidden';
    input.name = name;
    input.value = value;
    input.setAttribute('data-addon-input', '');
    return input;
  }

  /*
   * Image zoom modal, mirroring the sale block's dialog behaviour.
   * The trigger is a button rather than part of the label, and the click is
   * stopped so opening the zoom never ticks the add-on.
   */
  function bindImageModal(container) {
    var modal = container.parentElement
      ? container.parentElement.querySelector(SELECTORS.modal)
      : null;
    if (!modal) modal = document.querySelector(SELECTORS.modal);
    if (!modal) return;

    var modalImg = modal.querySelector(SELECTORS.modalImg);
    if (!modalImg) return;

    var open = function (src, alt) {
      if (!src) return;
      modalImg.src = src;
      modalImg.alt = alt || '';
      if (typeof modal.showModal === 'function') {
        modal.showModal();
      } else {
        modal.setAttribute('open', '');
      }
    };

    var close = function () {
      if (typeof modal.close === 'function') {
        modal.close();
      } else {
        modal.removeAttribute('open');
      }
      modalImg.removeAttribute('src');
      modalImg.alt = '';
    };

    container.querySelectorAll(SELECTORS.imageTrigger).forEach(function (trigger) {
      trigger.addEventListener('click', function (event) {
        event.preventDefault();
        event.stopPropagation();
        open(trigger.getAttribute('data-addon-image-src'), trigger.getAttribute('data-addon-image-alt'));
      });
    });

    if (modal.dataset.addonsModalBound !== 'true') {
      modal.dataset.addonsModalBound = 'true';

      var closeBtn = modal.querySelector(SELECTORS.modalClose);
      if (closeBtn) {
        closeBtn.addEventListener('click', function (event) {
          event.preventDefault();
          close();
        });
      }

      // Click on the backdrop (the dialog itself) closes it.
      modal.addEventListener('click', function (event) {
        if (event.target === modal) close();
      });
    }
  }

  function bind(container) {
    if (!container || container.__addOnsBound) return;
    container.__addOnsBound = true;

    container.addEventListener('change', function (event) {
      if (!event.target.closest(SELECTORS.checkbox)) return;
      syncForm(container);
    });

    // The T&C link sits inside a <label>, so a plain click would also toggle
    // the checkbox. Let the link navigate without changing the selection.
    container.addEventListener('click', function (event) {
      if (event.target.closest(SELECTORS.tcLink)) {
        event.stopPropagation();
      }
    });

    bindImageModal(container);

    // A cart add clears the selection so the add-ons are not silently added
    // again on the shopper's next add-to-cart.
    document.addEventListener('theme:product:added', function () {
      var checked = container.querySelectorAll(SELECTORS.checkbox + ':checked');
      if (!checked.length) return;
      checked.forEach(function (checkbox) {
        checkbox.checked = false;
      });
      syncForm(container);
    });

    // Reflect any state the browser restored on back/forward navigation.
    syncForm(container);
  }

  function init(root) {
    var scope = root && root.querySelectorAll ? root : document;
    scope.querySelectorAll(SELECTORS.container).forEach(bind);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init(document);
    });
  } else {
    init(document);
  }

  // Re-bind when the section is re-rendered in the theme editor or by a
  // variant change that replaces the product form markup.
  document.addEventListener('shopify:section:load', function (event) {
    init(event.target || document);
  });
})();